// Lifecycle stepper (DESIGN_SPEC §10.13): Conifer's numbered steps laid horizontally; vertical under 720px (CSS).
//   <Stepper steps label? className? />
//     steps: { label, state: "done" | "current" | "waiting" | "future" | "stopped", time?, duration?, note?, why?, t0? }[]
//       done = accent ring + number · current = live-cyan fill + a live pip beside the label (aria-current="step")
//       waiting = dashed ring, the current step of a queued run (nothing is running yet)
//       future = field ring, faint · stopped = ink ring with ×, plus the `why` line (e.g. "cancelled during boot · +09:12")
//       duration renders in ink-soft, `note` after it in faint ("0.67 s · timeout reached"); t0 tags the RUNNING step
//   Display states: QUEUED · PROVISIONING · BOOTING · RUNNING (T0) · COLLECTING · COMPLETED; a failure or cancel replaces the next step.
import type { ReactNode } from "react";
import { Pip } from "./Glyphs";
import { cx, vars } from "./cx";

type StepState = "done" | "current" | "waiting" | "future" | "stopped";
export type Step = { label: string; state: StepState; time?: ReactNode; duration?: ReactNode; note?: ReactNode; why?: ReactNode; t0?: boolean };

export function Stepper({ steps, label = "Lifecycle", className }: { steps: readonly Step[]; label?: string; className?: string }) {
  return (
    <ol className={cx("steps", className)} style={vars({ "--n": steps.length })} aria-label={label}>
      {steps.map((s, i) => (
        <li key={`${s.label}-${i}`} className={cx("step", s.state !== "done" && `is-${s.state}`)} aria-current={s.state === "current" || s.state === "waiting" ? "step" : undefined}>
          <span className="step-n" aria-hidden="true">
            {s.state === "stopped" ? "×" : String(i + 1).padStart(2, "0")}
          </span>
          <span className="step-l">
            {s.label}
            {s.t0 && <span className="t0">T0</span>}
            {s.state === "current" && <Pip state="still" />}
          </span>
          {s.time !== undefined && <span className="step-t">{s.time}</span>}
          {(s.duration !== undefined || s.note !== undefined) && (
            <span className="step-d">
              {s.duration !== undefined && <b>{s.duration}</b>}
              {s.duration !== undefined && s.note !== undefined && " · "}
              {s.note}
            </span>
          )}
          {s.why && <span className="step-why block">{s.why}</span>}
        </li>
      ))}
    </ol>
  );
}
